import { useState } from "react";
import { FileUp, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useFetch } from "../lib/useFetch";
import { api, asList, errorText } from "../lib/api";

const BLANK = { title: "", volume: 14, number: "", season: "", summary: "", editor_note: "" };

function upload(path, files, onDone, setBusy) {
  const fd = new FormData();
  Object.entries(files).forEach(([k, v]) => { if (v instanceof File) fd.append(k, v); else if (v !== undefined) fd.append(k, v); });
  setBusy(true);
  return api.post(path, fd, { timeout: 600000 })
    .then((r) => { onDone(r.data); })
    .catch((e) => toast.error(errorText(e)))
    .finally(() => setBusy(false));
}

function IssueRow({ is, onChange, onDelete }) {
  const [busy, setBusy] = useState(false);
  const pick = (kind) => (e) => {
    const f = e.target.files?.[0];
    if (f) upload(`/admin/issues/${is.id}/files`, { [kind]: f }, (d) => { onChange(d); toast.success(kind === "pdf" ? "PDF uploaded — the reader now shows the printed pages" : "Cover updated"); }, setBusy);
    e.target.value = "";
  };
  return (
    <div className="py-5 flex gap-5 items-center" data-testid={`admin-issue-${is.id}`}>
      <img src={is.cover} alt="" className="w-14 h-20 object-cover bg-[var(--paper-2)]" />
      <div className="flex-1 min-w-0">
        <p className="f-mono text-xs text-[var(--muted)]">Vol. {is.volume} No. {is.number} · {is.season}</p>
        <p className="f-serif text-xl truncate">{is.title}</p>
        <p className="text-sm mt-1 flex items-center gap-1.5" data-testid={`admin-issue-pdf-status-${is.id}`}>
          {is.pdf_pages ? <><CheckCircle2 size={14} className="text-[var(--red)]" /> {is.pdf_name} · {is.pdf_pages} pages (first {is.preview_pages} free)</> : <span className="text-[var(--muted)]">No PDF yet — reader shows the web edition</span>}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <label className="btn-line h-9 px-3 text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer" data-testid={`admin-issue-upload-pdf-${is.id}`}>
          <FileUp size={14} /> {busy ? "Uploading…" : is.pdf_pages ? "Replace PDF" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" disabled={busy} onChange={pick("pdf")} />
        </label>
        <label className="h-9 px-3 text-xs border rule inline-flex items-center cursor-pointer hover:border-[var(--ink)]" data-testid={`admin-issue-upload-cover-${is.id}`}>
          Cover<input type="file" accept="image/*" className="hidden" disabled={busy} onChange={pick("cover")} />
        </label>
        <button onClick={() => onDelete(is.id)} className="p-2 text-[var(--muted)] hover:text-[var(--red)]" aria-label="Delete issue" data-testid={`admin-issue-delete-${is.id}`}><Trash2 size={16} /></button>
      </div>
    </div>
  );
}

export function IssuesAdmin() {
  const { data, setData } = useFetch("/issues", []);
  const list = asList(data);
  const [f, setF] = useState(BLANK);
  const [cover, setCover] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = (e) => {
    e.preventDefault();
    upload("/admin/issues", { ...f, cover, pdf: pdf || undefined }, (d) => {
      setData([d, ...list].sort((a, b) => b.volume - a.volume || b.number - a.number));
      setF(BLANK); setCover(null); setPdf(null); toast.success("Issue created");
    }, setBusy);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this issue? Its articles stay published.")) return;
    try { await api.delete(`/admin/issues/${id}`); setData(list.filter((i) => i.id !== id)); toast.success("Issue deleted"); }
    catch (e) { toast.error(errorText(e)); }
  };
  const replace = (d) => setData(list.map((i) => (i.id === d.id ? d : i)));
  return (
    <div className="grid lg:grid-cols-12 gap-12" data-testid="admin-issues">
      <form onSubmit={create} className="lg:col-span-4 space-y-5" data-testid="issue-create-form">
        <p className="f-serif text-2xl">New issue</p>
        <input required placeholder="Title" value={f.title} onChange={set("title")} className="field" data-testid="new-issue-title" />
        <div className="grid grid-cols-3 gap-4">
          <input required type="number" min={1} placeholder="Vol." value={f.volume} onChange={set("volume")} className="field" data-testid="new-issue-volume" />
          <input required type="number" min={1} placeholder="No." value={f.number} onChange={set("number")} className="field" data-testid="new-issue-number" />
          <input required placeholder="Month / season" value={f.season} onChange={set("season")} className="field col-span-1" data-testid="new-issue-season" />
        </div>
        <textarea rows={2} placeholder="Summary" value={f.summary} onChange={set("summary")} className="field resize-none" data-testid="new-issue-summary" />
        <textarea rows={3} placeholder="Editor's letter" value={f.editor_note} onChange={set("editor_note")} className="field resize-none" />
        <label className="block text-sm"><span className="eyebrow">Cover image *</span><input required type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0] || null)} className="mt-2 block text-sm" data-testid="new-issue-cover" /></label>
        <label className="block text-sm"><span className="eyebrow">Issue PDF (max 60 MB)</span><input type="file" accept="application/pdf" onChange={(e) => setPdf(e.target.files?.[0] || null)} className="mt-2 block text-sm" data-testid="new-issue-pdf" /></label>
        <button disabled={busy} className="btn-red h-11 px-8 font-semibold disabled:opacity-50" data-testid="new-issue-submit">{busy ? "Uploading…" : "Create issue"}</button>
      </form>
      <div className="lg:col-span-8 divide-y rule border-y rule self-start">
        {list.map((is) => <IssueRow key={is.id} is={is} onChange={replace} onDelete={remove} />)}
      </div>
    </div>
  );
}

export function ReadersAdmin() {
  const { data, setData } = useFetch("/admin/readers", []);
  const list = asList(data);
  const toggle = async (u) => {
    const active = u.subscription?.status !== "active";
    try {
      await api.patch(`/admin/readers/${u.id}/access`, { active });
      setData(list.map((x) => (x.id === u.id ? { ...x, subscription: { ...(x.subscription || { plan_name: "Complimentary", source: "editor" }), status: active ? "active" : "inactive" } } : x)));
      toast.success(active ? "Access granted" : "Access removed");
    } catch (e) { toast.error(errorText(e)); }
  };
  if (!list.length) return <p className="py-16 text-center text-[var(--muted)]">No reader accounts yet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="admin-readers">
      <table className="w-full text-sm">
        <thead><tr className="text-left eyebrow border-b border-[var(--ink)]"><th className="py-3">Reader</th><th>Plan</th><th>Source</th><th>Status</th><th /></tr></thead>
        <tbody className="divide-y rule">
          {list.map((u) => {
            const on = u.subscription?.status === "active";
            return (
              <tr key={u.id} data-testid={`reader-row-${u.id}`}>
                <td className="py-3"><p className="font-medium">{u.name}</p><p className="text-[var(--muted)]">{u.email}</p></td>
                <td>{u.subscription?.plan_name || "—"}</td>
                <td>{u.subscription?.source || "—"}</td>
                <td><span className={`px-2 py-0.5 text-xs ${on ? "bg-[#E3E9FA] text-[#002FA7]" : "bg-[var(--paper-2)]"}`}>{on ? "active" : "no access"}</span></td>
                <td className="text-right"><button onClick={() => toggle(u)} className="link-u text-sm" data-testid={`reader-toggle-${u.id}`}>{on ? "Remove access" : "Grant access"}</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
